import { useState, useEffect, useMemo } from "react";
import tw from "tailwind-styled-components";
import { useMutation, useQuery } from "react-query";
import axios from "axios";
import Config from "../../Config";
import Loading from "../../Components/Loading";
import {
    BoxContainer,
    BoxTitle,
    BoxHead,
    Underline,
    FilterTableWrapper,
    FilterWrapperWithSearch,
} from "../../Components/Styles/PageStyles";
import NotFoundModel from "../../Components/NotFoundModel";
import { useSelector } from "react-redux";
import moment from "moment";
import { toast } from "react-toastify";
import SideModal from "../../Components/SideModal";
import AddAutoPayDetails from "../../Components/Payout/AutoPay/AddAutoPayDetails";
import Table from "../../Components/Payout/AutoPay/Table";
import ViewDetails from "../../Components/Payout/AutoPay/ViewDetails";
import ViewTransfers from "../../Components/Payout/AutoPay/ViewTransfers";
import SearchFilterBox from "../../Components/SearchFilterBox";
import SummaryCards from "../../Components/Payout/AutoPay/SummaryCards";
import { SummarySkeletonLoading } from "../../Components/SkeletonLoading";
import ChangeStatus from "../../Components/Payout/AutoPay/ChangeStatus";

const AutoPay = () => {
    const searchOptions = [
        { label: "Name", value: "beneficiary" },
        { label: "Mobile", value: "mobile" },
    ];
    const [key, setKey] = useState("");
    const user = useSelector((state) => state.UserReducer.user);
    const [selectedData, setSelectedData] = useState("");
    const [apiData, setApiData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [isFocused, setIsFocused] = useState(false);
    const [isAddAutoPay, setIsAddAutoPay] = useState(false);
    const [isChangeStatus, setIsChangeStatus] = useState(false);
    const [viewDetails, setViewDetails] = useState(false);
    const [beneficiariesData, setBeneficiaries] = useState([]);
    const [categories, setCategories] = useState([]);
    const [apiTransferData, setTransferApiData] = useState([]);
    const [showTransfersModal, setShowTransfersModal] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [filterType, setFilterType] = useState(null);
    const [selectedFilter, setSelectedFilter] = useState("ACTIVE");

    const [summary, setSummary] = useState({
        all: 0,
        active: 0,
        upcomingPayments: 0,
        monthlyAmount: 0,
        todayPayout: 0,
        completed: 0,
        cancelled: 0,
    });

    const fetchFunction = async (params) => {
        const response = await axios.get(`${Config.apiUrl}/autopay/list`, {
            headers: {
                authorization: "Bearer " + user.token,
                "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
            },
        });
        return response.data;
    };

    const getSuccess = (res) => {
        if (res.isSuccess) {
            setApiData(res.autopays);
            const activeData = res.autopays.filter(
                item => item.status === Config.AUTOPAY_STATUS.ACTIVE
            );
            setFilteredData(activeData);
            setCategories(res?.categories);
            setBeneficiaries(res?.beneficiaries);
            setIsFocused(true);

            // Calculate summary
            calculateSummary(res.autopays);
        }
    };

    const getError = (err) => {
        toast.error(err?.response?.data?.message || "An Error Occured");
    };
    const {
        isLoading,
        isFetching,
        error,
        data,
        refetch,
    } = useQuery(
        ['autopayList'],
        fetchFunction,
        {
            onSuccess: getSuccess,
            onError: getError,
        }
    );

    useEffect(() => {
        refetch();
    }, []);

    const getFilteredAutoPays = (filter, data = apiData) => {
        if (!Array.isArray(data)) {
            return [];
        }

        if (filter === "ALL") {
            return data;
        }

        const currentDate = moment();

        return data.filter((item) => {
            switch (filter) {
                case "ACTIVE":
                    return item.status === Config.AUTOPAY_STATUS.ACTIVE;

                case "TODAY_PAYOUT":
                    return (
                        item.status === Config.AUTOPAY_STATUS.ACTIVE &&
                        item.nextDueDate &&
                        moment(item.nextDueDate).isSame(currentDate, "day")
                    );

                case "UPCOMING":
                    return (
                        item.status === Config.AUTOPAY_STATUS.ACTIVE &&
                        item.nextDueDate &&
                        moment(item.nextDueDate).isSame(currentDate, "month")
                    );

                case "THIS_MONTH":
                    if (item.status !== Config.AUTOPAY_STATUS.ACTIVE) {
                        return false;
                    }

                    return (
                        (item.nextDueDate &&
                            moment(item.nextDueDate).isSame(currentDate, "month")) ||
                        (item.lastPaidDate &&
                            moment(item.lastPaidDate).isSame(currentDate, "month"))
                    );

                case "COMPLETED":
                    return item.status === Config.AUTOPAY_STATUS.COMPLETED;

                case "CANCELLED":
                    return item.status === Config.AUTOPAY_STATUS.CANCELLED;

                default:
                    return true;
            }
        });
    };

    // Calculate summary data
    const calculateSummary = (data) => {
        const currentDate = moment();
        const currentMonth = currentDate.format("YYYY-MM");

        const activeAutoPays =
            data?.filter(
                item => item.status === Config.AUTOPAY_STATUS.ACTIVE
            ) || [];

        const allCount = data?.length || 0;
        //const activeCount = data?.filter(item => item.status === Config.AUTOPAY_STATUS.ACTIVE)?.length || 0;
        const activeCount = activeAutoPays.length;

        const completedCount = data?.filter(item => item.status === Config.AUTOPAY_STATUS.COMPLETED)?.length || 0;

        const cancelledCount =
            data?.filter(
                item => item.status === Config.AUTOPAY_STATUS.CANCELLED
            )?.length || 0;

        // Calculate upcoming amount (active and nextDueDate in current month)
        const upcomingPayments = data?.filter(item => {
            if (item.status !== Config.AUTOPAY_STATUS.ACTIVE) return false;
            if (!item.nextDueDate) return false;
            const dueMonth = moment(item.nextDueDate).format("YYYY-MM");
            return dueMonth === currentMonth;
        })?.reduce((sum, item) => sum + Number(item.amount || 0), 0) || 0;

        // Calculate schedule payments
        const monthlyAmount = data?.filter(item => {
            // Check if nextDueDate is in current month
            let hasCurrentMonthDate = false;
            if (item.status !== Config.AUTOPAY_STATUS.ACTIVE) return false;

            if (item.nextDueDate) {
                const dueMonth = moment(item.nextDueDate).format("YYYY-MM");
                if (dueMonth === currentMonth) {
                    hasCurrentMonthDate = true;
                }
            }

            // Check if lastPaidDate is in current month
            if (item.lastPaidDate) {
                const paidMonth = moment(item.lastPaidDate).format("YYYY-MM");
                if (paidMonth === currentMonth) {
                    hasCurrentMonthDate = true;
                }
            }

            return hasCurrentMonthDate;
        })?.reduce((sum, item) => sum + Number(item.amount || 0), 0) || 0;

        const todayPayout = activeAutoPays
            .filter(item => {
                return (
                    item.nextDueDate &&
                    moment(item.nextDueDate).isSame(currentDate, "day")
                );
            })
            .reduce((sum, item) => sum + Number(item.amount || 0), 0);
        // const monthlyAmount = data?.filter(item => {
        //     if (item.status !== Config.AUTOPAY_STATUS.ACTIVE) return false;
        //     if (!item.nextDueDate) return false;
        //     const dueDate = moment(item.nextDueDate);
        //     return dueDate.isAfter(currentDate, 'day');
        // })?.length || 0;
        // })?.reduce((sum, item) => sum + Number(item.amount || 0), 0) || 0;


        setSummary({
            all: allCount,
            active: activeCount,
            upcomingPayments: upcomingPayments,
            monthlyAmount: monthlyAmount,
            todayPayout,
            completed: completedCount,
            cancelled: cancelledCount
        });
    };

    // Handle summary card click
    const onSummaryBoxClickTrash = (filter) => {
        setSelectedFilter(filter);

        if (filter === "ALL") {
            setFilteredData(apiData);
            return;
        }

        const currentDate = moment();
        const currentMonth = currentDate.format("YYYY-MM");

        const filtered = apiData.filter((item) => {
            if (filter === "ACTIVE") {
                return item.status === Config.AUTOPAY_STATUS.ACTIVE;
            }
            if (filter === "UPCOMING") {
                if (item.status !== Config.AUTOPAY_STATUS.ACTIVE) return false;
                if (!item.nextDueDate) return false;
                const dueMonth = moment(item.nextDueDate).format("YYYY-MM");
                return dueMonth === currentMonth;
            }
            // if (filter === "UPCOMING") {
            //     if (item.status !== Config.AUTOPAY_STATUS.ACTIVE) return false;
            //     if (!item.nextDueDate) return false;
            //     const dueDate = moment(item.nextDueDate);
            //     return dueDate.isAfter(currentDate, 'day');
            // }
            if (filter === "THIS_MONTH") {
                // Check if nextDueDate OR lastPaidDate is in current month
                let hasCurrentMonthDate = false;
                if (item.status !== Config.AUTOPAY_STATUS.ACTIVE) return false;
                if (item.nextDueDate) {
                    const dueMonth = moment(item.nextDueDate).format("YYYY-MM");
                    if (dueMonth === currentMonth) {
                        hasCurrentMonthDate = true;
                    }
                }

                if (item.lastPaidDate) {
                    const paidMonth = moment(item.lastPaidDate).format("YYYY-MM");
                    if (paidMonth === currentMonth) {
                        hasCurrentMonthDate = true;
                    }
                }

                return hasCurrentMonthDate;
            }
            if (filter === "COMPLETED") {
                return item.status === Config.AUTOPAY_STATUS.COMPLETED;
            }
            if (filter === "CANCELLED") {
                return item.status === Config.AUTOPAY_STATUS.CANCELLED;
            }
            return true;
        });

        setFilteredData(filtered);
    };

    const onSummaryBoxClick = (filter) => {
        setSelectedFilter(filter);
        setFilteredData(getFilteredAutoPays(filter));
    };

    // Handle search filter change from SearchFilterBox
    const handleFilterChangeTrash = (values) => {
        const { filterType, searchQuery } = values;
        setFilterType(filterType);
        setSearchQuery(searchQuery);

        if (!searchQuery || searchQuery.trim() === "") {
            // If no search, apply summary filter if any
            if (selectedFilter !== "ALL") {
                applySummaryFilter(selectedFilter);
            } else {
                setFilteredData(apiData);
            }
            return;
        }

        const searchLower = searchQuery.toLowerCase().trim();

        let baseData = apiData;
        // Apply summary filter first if any
        if (selectedFilter !== "ALL") {
            const currentDate = moment();
            const currentMonth = currentDate.format("YYYY-MM");
            baseData = apiData.filter((item) => {
                if (selectedFilter === "ACTIVE") {
                    return item.status === Config.AUTOPAY_STATUS.ACTIVE;
                }
                if (selectedFilter === "UPCOMING") {
                    if (item.status !== Config.AUTOPAY_STATUS.ACTIVE) return false;
                    if (!item.nextDueDate) return false;
                    const dueMonth = moment(item.nextDueDate).format("YYYY-MM");
                    return dueMonth === currentMonth;
                }
                // if (selectedFilter === "UPCOMING") {
                //     if (item.status !== Config.AUTOPAY_STATUS.ACTIVE) return false;
                //     if (!item.nextDueDate) return false;
                //     const dueDate = moment(item.nextDueDate);
                //     return dueDate.isAfter(currentDate, 'day');
                // }
                if (selectedFilter === "THIS_MONTH") {
                    // Check if nextDueDate OR lastPaidDate is in current month
                    let hasCurrentMonthDate = false;
                    if (item.status !== Config.AUTOPAY_STATUS.ACTIVE) return false;

                    if (item.nextDueDate) {
                        const dueMonth = moment(item.nextDueDate).format("YYYY-MM");
                        if (dueMonth === currentMonth) {
                            hasCurrentMonthDate = true;
                        }
                    }

                    if (item.lastPaidDate) {
                        const paidMonth = moment(item.lastPaidDate).format("YYYY-MM");
                        if (paidMonth === currentMonth) {
                            hasCurrentMonthDate = true;
                        }
                    }

                    return hasCurrentMonthDate;
                }
                if (selectedFilter === "COMPLETED") {
                    return item.status === Config.AUTOPAY_STATUS.COMPLETED;
                }
                if (selectedFilter === "CANCELLED") {
                    return item.status === Config.AUTOPAY_STATUS.CANCELLED;
                }
                return true;
            });
        }

        const filtered = baseData.filter((item) => {
            // Search by beneficiary name
            if (filterType === "beneficiary") {
                const beneficiaryName = (item?.beneficiaryName || "").toLowerCase();
                return beneficiaryName.includes(searchLower);
            }
            // Search by mobile
            else if (filterType === "mobile") {
                const beneficiaryMobile = (item?.beneficiaryMobile || "").toLowerCase();
                return beneficiaryMobile.includes(searchLower);
            }
            // If no filter type, search both
            else {
                const beneficiaryName = (item?.beneficiaryName || "").toLowerCase();
                const beneficiaryMobile = (item?.beneficiaryMobile || "").toLowerCase();
                return beneficiaryName.includes(searchLower) ||
                    beneficiaryMobile.includes(searchLower);
            }
        });

        setFilteredData(filtered);
    };

    const handleFilterChange = (values) => {
        const { filterType, searchQuery } = values;

        setFilterType(filterType);
        setSearchQuery(searchQuery);

        const baseData = getFilteredAutoPays(selectedFilter);

        if (!searchQuery || searchQuery.trim() === "") {
            setFilteredData(baseData);
            return;
        }

        const searchLower = searchQuery.toLowerCase().trim();

        const filtered = baseData.filter((item) => {
            const beneficiaryName =
                (item?.beneficiaryName || "").toLowerCase();

            const beneficiaryMobile =
                (item?.beneficiaryMobile || "").toLowerCase();

            if (filterType === "beneficiary") {
                return beneficiaryName.includes(searchLower);
            }

            if (filterType === "mobile") {
                return beneficiaryMobile.includes(searchLower);
            }

            return (
                beneficiaryName.includes(searchLower) ||
                beneficiaryMobile.includes(searchLower)
            );
        });

        setFilteredData(filtered);
    };

    // Apply summary filter
    const applySummaryFilterTrash = (filter) => {
        setSelectedFilter(filter);

        if (filter === "ALL") {
            setFilteredData(apiData);
            return;
        }

        const currentDate = moment();
        const currentMonth = currentDate.format("YYYY-MM");

        const filtered = apiData.filter((item) => {
            if (filter === "ACTIVE") {
                return item.status === Config.AUTOPAY_STATUS.ACTIVE;
            }
            if (filter === "UPCOMING") {
                if (item.status !== Config.AUTOPAY_STATUS.ACTIVE) return false;
                if (!item.nextDueDate) return false;
                const dueMonth = moment(item.nextDueDate).format("YYYY-MM");
                return dueMonth === currentMonth;
            }
            if (filter === "THIS_MONTH") {
                // Check if nextDueDate OR lastPaidDate is in current month
                let hasCurrentMonthDate = false;
                if (item.status !== Config.AUTOPAY_STATUS.ACTIVE) return false;

                if (item.nextDueDate) {
                    const dueMonth = moment(item.nextDueDate).format("YYYY-MM");
                    if (dueMonth === currentMonth) {
                        hasCurrentMonthDate = true;
                    }
                }

                if (item.lastPaidDate) {
                    const paidMonth = moment(item.lastPaidDate).format("YYYY-MM");
                    if (paidMonth === currentMonth) {
                        hasCurrentMonthDate = true;
                    }
                }

                return hasCurrentMonthDate;
            }
            // if (filter === "UPCOMING") {
            //     if (item.status !== Config.AUTOPAY_STATUS.ACTIVE) return false;
            //     if (!item.nextDueDate) return false;
            //     const dueDate = moment(item.nextDueDate);
            //     return dueDate.isAfter(currentDate, 'day');
            // }
            if (filter === "COMPLETED") {
                return item.status === Config.AUTOPAY_STATUS.COMPLETED;
            }
            if (filter === "CANCELLED") {
                return item.status === Config.AUTOPAY_STATUS.CANCELLED;
            }
            return true;
        });

        setFilteredData(filtered);
    };

    const applySummaryFilter = (filter) => {
        setSelectedFilter(filter);
        setFilteredData(getFilteredAutoPays(filter));
    };

    // Handle clear filters
    const handleClearFiltersTrash = () => {
        setSearchQuery("");
        setFilterType(null);
        setSelectedFilter("ACTIVE");
        setFilteredData(apiData);
    };

    const handleClearFilters = () => {
        setSearchQuery("");
        setFilterType(null);
        setSelectedFilter("ACTIVE");
        setFilteredData(getFilteredAutoPays("ACTIVE"));
    };

    //------- Add AutoPay -------
    const addAutoPayFunction = async (values) => {
        return await axios.post(`${Config.apiUrl}/autopay/create`, values, {
            headers: {
                authorization: "Bearer" + " " + user.token,
                "Content-Type": "application/json",
            },
        });
    };

    const onAddAutoPaySuccess = (data) => {
        if (data?.data?.isSuccess === true) {
            toast.success(data?.data?.message || "Success");
            setIsAddAutoPay(false);
            refetch(); // Refetch the list after adding autopay
        } else {
            toast.error(data?.data?.message || "An Error Occured");
        }
    };

    const onAddAutoPayError = (data) => {
        toast.error(data?.response?.data?.message || "An Error Occured");
    };

    const { isLoading: addAutoPayLoading, mutate: addAutoPayMutate } = useMutation(
        addAutoPayFunction,
        {
            onSuccess: onAddAutoPaySuccess,
            onError: onAddAutoPayError,
        }
    );
    const handleAddAutoPayClose = () => {
        setIsAddAutoPay(false);
    };

    //------- Change Status -------
    const changeStatusFunction = async (values) => {
        return await axios.post(`${Config.apiUrl}/autopay/update/status`, values, {
            headers: {
                authorization: "Bearer" + " " + user.token,
                "Content-Type": "application/json",
            },
        });
    };

    const onChangeStatusSuccess = (data) => {
        if (data?.data?.isSuccess === true) {
            toast.success(data?.data?.message || "Success");
            setIsChangeStatus(false);
            refetch(); // Refetch the list after status change
        } else {
            toast.error(data?.data?.message || "An Error Occured");
        }
    };

    const onChangeStatusError = (data) => {
        toast.error(data?.response?.data?.message || "An Error Occured");
    };

    const { isLoading: changeStatusLoading, mutate: changeStatusMutate } = useMutation(
        changeStatusFunction,
        {
            onSuccess: onChangeStatusSuccess,
            onError: onChangeStatusError,
        }
    );
    const handleChangeStatusClose = () => {
        setIsChangeStatus(false);
    };

    //Transfer Details API
    const fetchTransfersDetail = async (autopayId) => {
        const params = new URLSearchParams();
        params.append("autopayId", autopayId);
        const query = params.toString();

        const response = await axios.get(
            `${Config.apiUrl}/autopay/list/transactions?${query}`,
            {
                headers: {
                    authorization: `Bearer ${user.token}`,
                    "Content-Type":
                        "application/x-www-form-urlencoded; charset=UTF-8",
                },
            }
        );

        return response.data;
    };

    const getTransferListSuccess = (res) => {
        if (res?.data === false) {
            setTransferApiData([]);
            return;
        }
        setTransferApiData(res?.data);
        setShowTransfersModal(true);
    };

    const getTransferListError = () => {
        //setApiData([]);
    };

    const {
        isLoading: isTransferListLoading,
        mutate: getTransferListMutate,
        error: errorTransferList,
        data: dataTransferList,
        isRefetching: isTransferListRefetching,
    } = useMutation(fetchTransfersDetail, {
        onSuccess: getTransferListSuccess,
        onError: getTransferListError,
    });

    // Prepare summary data for SummaryCards component
    const summaryData = {
        all: summary.all,
        active: summary.active,
        upcomingPayments: summary.upcomingPayments,
        monthlyAmount: summary.monthlyAmount,
        todayPayout: summary.todayPayout,
        completed: summary.completed,
        cancelled: summary.cancelled,
    };

    return (
        <BoxContainer>
            <BoxHead>
                <BoxTitle className="!mb-0 whitespace-nowrap">
                    Manage Auto Pays
                    <Underline />
                </BoxTitle>
                <div className="flex space-x-4 md:ml-auto">
                    <AddBtn
                        type="button"
                        onClick={() => {
                            setKey(moment().unix());
                            setIsAddAutoPay(true);
                        }}
                    >
                        Setup Autopay
                    </AddBtn>
                </div>
            </BoxHead>
            {(isLoading || isFetching) && (
                <SummarySkeletonLoading count={5} />
            )}
            <SideModal
                isOpen={isAddAutoPay}
                onClose={() => setIsAddAutoPay(false)}
            >
                <AddAutoPayDetails
                    addAutoPayMutate={addAutoPayMutate}
                    addAutoPayLoading={addAutoPayLoading}
                    handleClose={handleAddAutoPayClose}
                    beneficiariesData={beneficiariesData}
                    expCategories={categories}
                />
            </SideModal>

            <SideModal
                isOpen={isChangeStatus}
                onClose={() => setIsChangeStatus(false)}
            >
                <ChangeStatus
                    changeStatusMutate={changeStatusMutate}
                    changeStatusLoading={changeStatusLoading}
                    handleClose={handleChangeStatusClose}
                    selectedData={selectedData}
                />
            </SideModal>

            <SideModal
                isOpen={viewDetails}
                onClose={() => setViewDetails(false)}
            >
                <ViewDetails
                    selectedData={selectedData}
                    setViewDetails={setViewDetails}
                />
            </SideModal>

            <SideModal
                isOpen={showTransfersModal}
                onClose={() => setShowTransfersModal(false)}
            >
                <ViewTransfers
                    setViewModal={setShowTransfersModal}
                    selectedData={selectedData}
                    data={apiTransferData}
                />
            </SideModal>

            {!isLoading && !isFetching && summaryData && (
                <SummaryCards
                    summaryData={summaryData}
                    onClick={onSummaryBoxClick}
                />
            )}
            <FilterTableWrapper>
                <FilterWrapperWithSearch>
                    <div className="flex md:space-x-4 md:flex-row flex-col md:gap-0 gap-4">
                        <div className="flex items-center">
                            <Text>
                                Total Records:{" "}
                                <span className="font-semibold">
                                    {filteredData?.length || 0}
                                </span>
                            </Text>
                        </div>
                    </div>
                    <div className="flex gap-2 justify-end items-center">
                        <SearchFilterBox
                            options={searchOptions}
                            handleFilterChange={handleFilterChange}
                            handleClearFilters={handleClearFilters}
                        />
                    </div>
                </FilterWrapperWithSearch>
                {!error && !isLoading && data && !isFetching && (
                    <TableWrapper>
                        <Table
                            apiData={filteredData || []}
                            setSelectedData={setSelectedData}
                            selectedData={selectedData}
                            setViewDetails={setViewDetails}
                            getTransferListMutate={getTransferListMutate}
                            setIsChangeStatus={setIsChangeStatus}
                        />
                        {(!filteredData || filteredData.length === 0) && <NotFoundModel />}
                    </TableWrapper>
                )}
            </FilterTableWrapper>
            {(isLoading || isFetching) && <Loading />}
        </BoxContainer>
    );
};

const TableWrapper = tw.div`mt-4 bg-white rounded-md `;
const AddBtn = tw.button`whitespace-nowrap flex space-x-1.5 justify-center items-center text-white h-full rounded-md px-6 cursor-pointer bg-gradient-to-r from-primary-color to-[#00A0FF] hover:brightness-110 py-2.5 text-sm`;
const Text = tw.p`text-gray-500 text-sm font-normal`;

export default AutoPay;